import Const from 'const';
import RuleBuilderError from './rule-builder-error';

interface RuleStructure {
    readonly obj: number | null;
    readonly type: number | null;
    readonly row: object;
}

class RuleBuilder {
    /** Type of rule: equal, at least, at most */
    private type: number | null = null;

    private obj: number | null = null;

    /** Object with variable name and its constant. E.g. {x123: 2, g456: -4}. */
    private vars: object = {};

    public static fromAtLeast(value: number, vars: object = {}): RuleBuilder {
        const builder = new RuleBuilder();
        builder.atLeast(value);
        builder.addVars(vars);

        return builder;
    }

    public static fromAtMost(value: number, vars: object = {}): RuleBuilder {
        const builder = new RuleBuilder();
        builder.atMost(value);
        builder.addVars(vars);

        return builder;
    }

    public static fromExactly(value: number, vars: object = {}): RuleBuilder {
        const builder = new RuleBuilder();
        builder.exactly(value);
        builder.addVars(vars);

        return builder;
    }

    public static fromVars(vars: object): RuleBuilder {
        const builder = new RuleBuilder();
        builder.addVars(vars);

        return builder;
    }

    public atLeast(value: number): this {
        this.obj = value;
        this.type = Const.STACKS_NUMBER_TYPE_AT_LEAST;

        return this;
    }

    public atMost(value: number): this {
        this.obj = value;
        this.type = Const.STACKS_NUMBER_TYPE_AT_MOST;

        return this;
    }

    public exactly(value: number): this {
        this.obj = value;
        this.type = Const.STACKS_NUMBER_TYPE_EXACTLY;

        return this;
    }

    public addVars(vars: object): this {
        this.vars = {
            ...this.vars,
            ...vars,
        };

        return this;
    }

    public addVar(name: string, constant: number): this {
        return this.addVars({[name]: constant});
    }

    public isRuleCreatable(): boolean {
        try {
            this.assertConfigurationIsValid();
        } catch (e) {
            return false;
        }

        return true;
    }

    public createRule(): RuleStructure {
        this.assertConfigurationIsValid();

        return {
            obj: this.obj,
            type: this.type,
            row: {...this.vars},
        };
    }

    private hasObj(): boolean {
        return this.obj !== null;
    }

    private hasType(): boolean {
        return this.type !== null;
    }

    private hasVars(): boolean {
        return !isEmpty(this.vars);
    }

    private assertConfigurationIsValid(): void {
        if (!this.hasObj()) {
            throw new RuleBuilderError('Obj has to be specified.');
        }
        if (!this.hasType()) {
            throw new RuleBuilderError('Type has to be specified.');
        }
        if (!this.hasVars()) {
            throw new RuleBuilderError('Vars have to be specified.');
        }
    }
}

export default RuleBuilder;
